import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { type ReactNode, useEffect } from "react";
import { Route, Routes } from "react-router-dom";

import { AUTH_CODE_PATH } from "~/components/auth/auth-flow";
import { LoginScreen } from "~/components/auth/LoginScreen";
import { NotAdminScreen } from "~/components/auth/NotAdminScreen";
import { VerifyScreen } from "~/components/auth/VerifyScreen";
import { UNAUTHORIZED_EVENT } from "~/lib/auth-token";
import { useAuthStore } from "~/store/auth-store";

/**
 * Decides what the whole window shows: the spinner while the session hydrates, the
 * sign-in screen, a "not an editor" notice for a valid member account without the
 * admin flag, or the panel itself.
 *
 * The flag check here is a courtesy — every admin endpoint re-checks the database
 * row, so all this gate can leak is an empty shell.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const hydrate = useAuthStore((state) => state.hydrate);
  const handleUnauthorized = useAuthStore((state) => state.handleUnauthorized);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // The API client dispatches this window event on a 401, so `lib/api.ts` never has
  // to import the store.
  useEffect(() => {
    const listener = () => handleUnauthorized();
    window.addEventListener(UNAUTHORIZED_EVENT, listener);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, listener);
  }, [handleUnauthorized]);

  if (status === "loading") {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // The same surface the sign-in screen paints, so there is no colour flash.
          backgroundColor: "background.paper",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (status === "unauthenticated")
    return (
      <Routes>
        <Route path={AUTH_CODE_PATH} element={<VerifyScreen />} />
        {/* Preserve whatever URL an expired session was using; once authenticated,
            the admin routes take over again. */}
        <Route path="*" element={<LoginScreen />} />
      </Routes>
    );
  if (user && !user.is_admin) return <NotAdminScreen />;
  return children;
}
